import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  subscribeToBookings,
  updateBookingStatus,
} from "../services/bookingService";
import { logout } from "../services/authService";
import { useAuth } from "../context/AuthContext";
import { findService } from "../data/services";
import AdminNav from "../components/AdminNav";
import styles from "./AdminDashboard.module.css";

const TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "confirmed", label: "Confirmed" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const BADGE_CLASS = {
  pending: "badgePending",
  confirmed: "badgeConfirmed",
  completed: "badgeCompleted",
  cancelled: "badgeCancelled",
};

function formatCreatedAt(ts) {
  if (!ts?.toDate) return "";
  return ts.toDate().toLocaleString("en-NZ", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatPreferredDate(value) {
  if (!value) return "";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-NZ", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissionError, setPermissionError] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [pendingActionId, setPendingActionId] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribeToBookings(
      (data) => {
        setBookings(data);
        setLoading(false);
        setPermissionError(false);
      },
      () => {
        setLoading(false);
        setPermissionError(true);
      }
    );
    return unsubscribe;
  }, []);

  const filtered = useMemo(() => {
    if (activeTab === "all") return bookings;
    return bookings.filter((b) => b.status === activeTab);
  }, [bookings, activeTab]);

  const counts = useMemo(() => {
    const c = { all: bookings.length };
    for (const tab of TABS) {
      if (tab.key === "all") continue;
      c[tab.key] = bookings.filter((b) => b.status === tab.key).length;
    }
    return c;
  }, [bookings]);

  const handleStatusChange = async (id, status) => {
    setPendingActionId(id);
    try {
      await updateBookingStatus(id, status);
    } catch (err) {
      // The onSnapshot listener will simply keep showing the old
      // status if this fails — a toast could be added here later.
    } finally {
      setPendingActionId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Bookings</h1>
        <div className={styles.headerRight}>
          {user?.email && <span className={styles.emailTag}>{user.email}</span>}
          <button className={styles.logoutBtn} onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>

      <AdminNav />

      {permissionError && (
        <div className={styles.banner}>
          Can't load bookings — Firestore rules probably don't allow signed-in
          reads yet. Add an <code>allow read, update: if request.auth != null;</code>{" "}
          rule for the <code>bookings</code> collection in the Firebase console.
        </div>
      )}

      <div className={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label} {counts[tab.key] ? `(${counts[tab.key]})` : ""}
          </button>
        ))}
      </div>

      {!loading && !permissionError && filtered.length === 0 && (
        <p className={styles.empty}>No bookings here yet.</p>
      )}

      <div className={styles.list}>
        {filtered.map((b) => {
          const serviceInfo = findService(b.service);
          const badgeClass = BADGE_CLASS[b.status] || "badgePending";
          const isBusy = pendingActionId === b.id;

          return (
            <div key={b.id} className={styles.row}>
              <div className={styles.rowTop}>
                <div className={styles.who}>
                  <span className={styles.name}>{b.name}</span>
                  <div className={styles.metaRow}>
                    <a href={`tel:${b.phone}`}>{b.phone}</a>
                    {b.email && <a href={`mailto:${b.email}`}>{b.email}</a>}
                  </div>
                </div>
                <span className={`${styles.badge} ${styles[badgeClass]}`}>
                  {b.status}
                </span>
              </div>

              <div className={styles.metaRow}>
                <span>{serviceInfo?.label || b.service}</span>
                <span>Preferred: {formatPreferredDate(b.preferredDate)}</span>
                {b.address && <span>{b.address}</span>}
                {b.createdAt && <span>Sent {formatCreatedAt(b.createdAt)}</span>}
              </div>

              {b.notes && <div className={styles.notes}>{b.notes}</div>}

              <div className={styles.actions}>
                <button
                  className={styles.actionBtn}
                  disabled={isBusy || b.status === "confirmed"}
                  onClick={() => handleStatusChange(b.id, "confirmed")}
                >
                  Confirm
                </button>
                <button
                  className={styles.actionBtn}
                  disabled={isBusy || b.status === "completed"}
                  onClick={() => handleStatusChange(b.id, "completed")}
                >
                  Mark done
                </button>
                <button
                  className={`${styles.actionBtn} ${styles.actionDanger}`}
                  disabled={isBusy || b.status === "cancelled"}
                  onClick={() => handleStatusChange(b.id, "cancelled")}
                >
                  Cancel
                </button>
                {b.status !== "pending" && (
                  <button
                    className={styles.actionBtn}
                    disabled={isBusy}
                    onClick={() => handleStatusChange(b.id, "pending")}
                  >
                    Reset to pending
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AdminDashboard;
